import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useTags } from "../api/queries";
import type { Quest } from "../api/types";
import { tagIndex, tagName } from "../lib/tags";
import { formatDate } from "../lib/format";
import { FavoriteButton } from "./FavoriteButton";
import { PlayerLink } from "./PlayerLink";
import { StarRating } from "./StarRating";
import { Tag } from "./ui";

const STATUS_LABELS: Record<string, { key: string; className: string }> = {
  CONTENT_STATUS_DRAFT: { key: "quest.status.draft", className: "border-amber-400/40 text-amber-300" },
  CONTENT_STATUS_REJECTED: { key: "quest.status.rejected", className: "border-rust/50 text-rust" },
  CONTENT_STATUS_ARCHIVED: { key: "quest.status.archived", className: "border-ink-600 text-ink-400" },
};

export function QuestCard({ quest }: { quest: Quest }) {
  const { t } = useTranslation();
  // Теги хранятся кодами: название берётся из справочника и зависит от языка.
  const index = tagIndex(useTags().data?.tags);
  const rounds = quest.roundCount ?? 0;
  const status = quest.status ? STATUS_LABELS[quest.status] : undefined;

  return (
    // Карточка кликабельна целиком, но сама ссылкой не является: внутри есть
    // имя автора, которое ведёт к нему, а не к квесту, а вложенная ссылка —
    // невалидная разметка, которую браузер «чинит», ломая вёрстку. Поэтому
    // ссылка растянута поверх карточки отдельным слоем.
    //
    // На телефоне карточка горизонтальная: вертикальная с обложкой 16:10
    // занимала почти весь экран, и каталог из семи квестов превращался в
    // десять экранов пролистывания.
    <div
      className="panel group relative flex overflow-hidden transition-all hover:border-amber-400/40
                 hover:shadow-glow focus-within:border-amber-400/60 sm:flex-col"
    >
      <Link
        to={`/quests/${quest.id}`}
        className="absolute inset-0 z-10 rounded-2xl"
        aria-label={quest.title}
      />
      <div className="relative aspect-square w-28 shrink-0 overflow-hidden bg-ink-850 sm:aspect-[16/10] sm:w-auto">
        {quest.coverImageUrl ? (
          <img
            src={quest.coverImageUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover opacity-90 transition-transform duration-500
                       group-hover:scale-105 group-hover:opacity-100"
          />
        ) : (
          <div className="flex h-full items-center justify-center px-2 text-center text-xs text-ink-600 sm:text-sm">
            {t("quest.noCover")}
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 hidden h-24 bg-gradient-to-t from-ink-950 to-transparent sm:block" />

        {/* Значки на миниатюре размером в палец не читаются, поэтому на
            телефоне они переезжают в текстовую колонку. */}
        {quest.type === "QUEST_TYPE_DAILY" && (
          <span className="absolute left-3 top-3 hidden rounded-full bg-amber-400 px-2.5 py-1 text-[11px]
                           font-bold uppercase tracking-wide text-ink-950 sm:inline">
            {t("quest.daily")}
          </span>
        )}
        {/* «Весь мир» отмечен рядом с квестом дня, но контуром: это тоже
            вход для всех, но не событие дня. */}
        {quest.type === "QUEST_TYPE_WORLD" && (
          <span className="absolute left-3 top-3 hidden rounded-full border border-amber-400/70 bg-ink-950/80 px-2.5
                           py-1 text-[11px] font-bold uppercase tracking-wide text-amber-300 sm:inline">
            {t("quest.world")}
          </span>
        )}

        {status && (
          <span className={`chip absolute right-3 top-3 hidden bg-ink-950/85 sm:inline-flex ${status.className}`}>
            {t(status.key)}
          </span>
        )}
      </div>

      {/* Сердце лежит поверх растянутой ссылки, поэтому ему нужен свой слой:
          иначе клик по нему открывал бы квест. */}
      {quest.id && !status && (
        <FavoriteButton
          questId={quest.id}
          active={Boolean(quest.isFavorite)}
          className="absolute right-1.5 top-1.5 z-20 bg-ink-950/70 backdrop-blur sm:right-2.5 sm:top-2.5"
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-3 sm:gap-3 sm:p-4">
        {(quest.type === "QUEST_TYPE_DAILY" || quest.type === "QUEST_TYPE_WORLD" || status) && (
          <div className="flex flex-wrap items-center gap-1.5 sm:hidden">
            {quest.type === "QUEST_TYPE_DAILY" && (
              <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold uppercase
                               tracking-wide text-ink-950">
                {t("quest.daily")}
              </span>
            )}
            {quest.type === "QUEST_TYPE_WORLD" && (
              <span className="rounded-full border border-amber-400/70 px-2 py-0.5 text-[10px] font-bold uppercase
                               tracking-wide text-amber-300">
                {t("quest.world")}
              </span>
            )}
            {status && <span className={`chip ${status.className}`}>{t(status.key)}</span>}
          </div>
        )}

        <h3 className="line-clamp-2 font-sans text-base leading-snug text-ink-50 sm:line-clamp-none sm:text-xl">
          {quest.title}
        </h3>

        {quest.description && (
          <p className="line-clamp-2 text-xs text-ink-400 sm:text-sm">{quest.description}</p>
        )}

        {quest.tags && quest.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {/* На узкой карточке третий тег уезжает на свою строку и растит
                её без пользы. */}
            {quest.tags.slice(0, 2).map((slug) => (
              <Tag key={slug}>{tagName(index.get(slug), slug)}</Tag>
            ))}
            {quest.tags.length > 2 && (
              <Tag key={quest.tags[2]} className="hidden sm:inline-flex">
                {tagName(index.get(quest.tags[2]), quest.tags[2])}
              </Tag>
            )}
          </div>
        )}

        {/* Оценка и просмотры — то, по чему каталог сортируется, поэтому они
            видны прямо в списке, а не только на странице квеста. */}
        <div className="flex items-center gap-2 text-[11px] text-ink-500 sm:text-xs">
          <StarRating value={quest.rating ?? 0} size={13} />
          <span className="tabular-nums">
            {quest.ratingCount ? (quest.rating ?? 0).toFixed(1) : t("quest.notRated")}
          </span>
          <span className="text-ink-700">•</span>
          <span className="tabular-nums">{t("quest.views", { count: Number(quest.viewCount ?? 0) })}</span>
        </div>

        <div className="mt-auto flex items-center justify-between gap-2 pt-1 text-[11px] text-ink-500 sm:pt-2 sm:text-xs">
          <span>
            {t("quest.photos", { count: rounds })}
          </span>
          <span className="truncate">
            {quest.author?.username ? (
              <>
                {t("quest.by")}{" "}
                {/* Ссылка внутри карточки-ссылки: клик по имени должен вести
                    к автору, а не к квесту, поэтому всплытие останавливаем. */}
                <PlayerLink
                  userId={quest.author.id}
                  username={quest.author.username}
                  className="relative z-20 text-ink-400 underline decoration-ink-700 underline-offset-2"
                />
              </>
            ) : (
              formatDate(quest.createdAt)
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
